import { useEffect, useState } from "react";

import { useAsyncSwapStatusQuery, AsyncSwapStatus } from "@bridgesplit/abf-react";
import { skipToken } from "@reduxjs/toolkit/query";
import { TIME } from "@bridgesplit/utils";

interface AsyncSwapSettlementParams {
    redemptionId?: string;
    routeType?: string;
    // Some routes (e.g. GLAM) require a per-vault state PDA on the poll query. Forwarded verbatim.
    state?: string;
}

interface AsyncSwapSettlementState {
    status: AsyncSwapStatus | "idle";
    settlementTxHash?: string;
    error?: string;
    isPolling: boolean;
    isComplete: boolean;
    isFailed: boolean;
}

const POLL_INTERVAL_MS = 5 * TIME.SECOND * 1000;
const MAX_POLL_DURATION_MS = 5 * TIME.MINUTE * 1000;

/**
 * Hook to handle async swap settlement polling.
 * Follows Single Responsibility Principle - only handles settlement state and polling.
 *
 * Uses RTK Query's skip parameter to control polling lifecycle. The query is skipped when:
 * - Not enabled, missing parameters, timed out, or swap is done (completed/failed)
 * This ties polling directly to the query lifecycle as recommended for RTK Query.
 */
export function useAsyncSwapSettlement({
    redemptionId,
    routeType,
    state
}: AsyncSwapSettlementParams): AsyncSwapSettlementState {
    const [hasTimedOut, setHasTimedOut] = useState(false);
    const [shouldPoll, setShouldPoll] = useState(true);

    const hasInputs = !!(redemptionId && routeType);

    const shouldQuery = hasInputs && shouldPoll && !hasTimedOut;
    const queryArgs = shouldQuery ? { redemptionId, routeType, state } : skipToken;

    const { data } = useAsyncSwapStatusQuery(queryArgs, {
        pollingInterval: POLL_INTERVAL_MS
    });

    const isComplete = data?.status === AsyncSwapStatus.Completed;
    const apiFailed = data?.status === AsyncSwapStatus.Reversed || !!data?.error;
    const isTerminal = isComplete || apiFailed;

    useEffect(() => {
        if (!hasInputs) return;

        if (isTerminal) {
            setShouldPoll(false);
            return;
        }

        const timeoutId = setTimeout(() => {
            setHasTimedOut(true);
            setShouldPoll(false);
        }, MAX_POLL_DURATION_MS);

        return () => clearTimeout(timeoutId);
    }, [hasInputs, isTerminal, redemptionId, routeType]);

    const isFailed = !isComplete && (apiFailed || hasTimedOut);
    const isPolling = shouldQuery && !isTerminal;

    return {
        status: data?.status ?? "idle",
        settlementTxHash: data?.settlementTransactionHash,
        error:
            data?.error ?? (!isComplete && hasTimedOut ? "Settlement timeout - please check your wallet" : undefined),
        isPolling,
        isComplete,
        isFailed
    };
}
